import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchFieldChoices, previewChoices } from "../api/forms";
import { ApiError } from "../api/client";
import {
  isRemoteChoicesBy,
  resolveChoicesByConfig,
  toChoiceRows,
  type ChoiceItem,
  type ChoiceRow,
} from "../lib/forms/fieldOptions";
import { normalizeValue } from "../lib/forms/visibility";
import type { FieldDto } from "../types/field";
import type { ValuesByName } from "../types/visibility";

export function useRemoteFieldChoices(
  field: FieldDto,
  valuesByName: ValuesByName,
): {
  active: boolean;
  sourceEmpty: boolean;
  loading: boolean;
  error: string | null;
  rows: ChoiceRow[];
} {
  const { formId: routeFormId } = useParams();
  const remote = isRemoteChoicesBy(field);
  const config = resolveChoicesByConfig(field);
  const sourceValue = config
    ? normalizeValue(valuesByName[config.field] ?? "")
    : "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<ChoiceItem[]>([]);

  const formId = routeFormId?.trim() || field.form_id;
  const fieldId = field.id;
  const url = config?.url ?? "";
  const path = config?.path ?? "";
  const item = config?.item ?? "";
  const value = config?.value ?? "";

  useEffect(() => {
    if (!remote) {
      setChoices([]);
      setError(null);
      setLoading(false);
      return;
    }
    if (!sourceValue) {
      setChoices([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const result =
          formId && fieldId != null
            ? await fetchFieldChoices(formId, fieldId, sourceValue)
            : url
              ? await previewChoices({
                  url,
                  path,
                  item,
                  value,
                  source: sourceValue,
                })
              : { choices: [], error: "Нет ссылки на список" };
        if (cancelled) return;
        setChoices(result.choices);
        setError(result.error);
      } catch (err) {
        if (cancelled) return;
        setChoices([]);
        setError(
          err instanceof ApiError
            ? err.message || "Не удалось загрузить список"
            : "Не удалось загрузить список",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [remote, sourceValue, formId, fieldId, url, path, item, value]);

  return {
    active: remote,
    sourceEmpty: remote && !sourceValue,
    loading,
    error,
    rows: toChoiceRows(choices),
  };
}
